import { goSolidApi } from "@/composables/useGoSolidApi";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import { defineStore } from "pinia";
import type { Ref } from "vue";
import { computed, ref } from "vue";
import { IssuanceDataSource } from "./issuanceStatusUtils";

export type IssuanceProductionDataCountryMetadata = Record<
  string,
  unknown
> | null;

export interface IssuanceProductionDataSubRow {
  id: number;
  external_id?: string;
  period_start_at: string;
  period_end_at: string;
  value: number;
  unit: string;
  status: string;
  country_metadata?: IssuanceProductionDataCountryMetadata;
}

export interface IssuanceProductionDataMainRow {
  registry_device_id: number;
  registry_device?: {
    id: number;
    device_name: string;
    device_eic: string;
    device_power: number;
    technology_code: string;
    commercial_operation_date: string;
    company_id: number;
  };
  period_start_at: string;
  period_end_at: string;
  total_value: number;
  unit: string;
  subrows: IssuanceProductionDataSubRow[];
}

export interface IssuanceProductionDataQueryParams {
  registry_device_id?: number;
  data_source?: IssuanceDataSource;
  status?: string;
  period_start?: string;
  period_end?: string;
  country_code?: string;
  record_ids?: string;
}

export interface IssuanceProductionDataListResponse {
  issuance_data_main_rows: IssuanceProductionDataMainRow[];
  registry_devices: {
    id: number;
    device_name: string;
    device_eic: string;
    device_power: number;
    technology_code: string;
    commercial_operation_date: string;
    company_id: number;
  }[];
}

export const useIssuanceProductionDataStore = defineStore(
  "issuanceProductionData",
  () => {
    // State
    const mainRows: Ref<IssuanceProductionDataMainRow[]> = ref([]);
    const registryDevices = ref<any[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);

    // Record loading state (simplified)
    const recordLoadingStates = ref(new Set<number>());

    // Selection state
    const selectedSubrowIds = ref(new Set<number>());

    // Computed
    const selectedCount = computed(() => selectedSubrowIds.value.size);

    // Actions
    const fetchIssuanceProductionData = async (
      companyId: number,
      queryParams: IssuanceProductionDataQueryParams = {}
    ) => {
      loading.value = true;

      try {
        const params = new URLSearchParams();
        sanitizeQueryParams(params, queryParams);

        const response = await goSolidApi.get(
          `/issuance-reporting/companies/${companyId}/issuance-production-data?${params.toString()}`
        );

        const responseData: IssuanceProductionDataListResponse =
          response.data.data;
        mainRows.value = responseData.issuance_data_main_rows;
        registryDevices.value = responseData.registry_devices;

        // Clear selection when data refreshes
        clearSelection();
      } catch (err: any) {
        // TODO: Use global error handling
        console.error("Error fetching issuance production data:", err);
      } finally {
        loading.value = false;
      }
    };

    const refreshData = async (companyId: number) => {
      await fetchIssuanceProductionData(companyId);
    };

    const updateIssuanceProductionRecord = async (
      companyId: number,
      recordId: number,
      updateData: Partial<IssuanceProductionDataSubRow>
    ) => {
      // Simple API call without validation/error handling (handled in component)
      const response = await goSolidApi.put(
        `/issuance-reporting/companies/${companyId}/issuance-production-data/${recordId}`,
        updateData
      );

      // Update local state optimistically
      updateSubrowInMainRows(recordId, response.data.data);
      return response.data.data;
    };

    const createIssuanceProductionRecord = async (
      companyId: number,
      deviceId: number,
      totalProduction: number,
      date: string
    ) => {
      const recordDate = new Date(date);

      const payload = {
        registry_device_id: deviceId,
        data_source: IssuanceDataSource.MANUAL,
        period_start_at: recordDate.toISOString(),
        period_end_at: recordDate.toISOString(),
        value: totalProduction,
        unit: "MWh",
        status: "pending",
      };

      // Make API call
      const response = await goSolidApi.post(
        `/issuance-reporting/companies/${companyId}/issuance-production-data`,
        payload
      );

      // Add new record to local state
      addSubrowToMainRows(response.data.data, deviceId);
      return response.data.data;
    };

    const deleteIssuanceProductionRecord = async (
      companyId: number,
      recordId: number
    ) => {
      // Make API call
      await goSolidApi.delete(
        `/issuance-reporting/companies/${companyId}/issuance-production-data/${recordId}`
      );

      // Remove record from local state
      removeSubrowFromMainRows(recordId);
    };

    const removeSubrowFromMainRows = (recordId: number) => {
      // Find and remove the specific subrow from mainRows
      for (const mainRow of mainRows.value) {
        const subrowIndex = mainRow.subrows.findIndex(
          (subrow) => subrow.id === recordId
        );
        if (subrowIndex !== -1) {
          // Remove the subrow
          mainRow.subrows.splice(subrowIndex, 1);

          // Recalculate the main row total value
          mainRow.total_value = mainRow.subrows.reduce(
            (sum, subrow) => sum + subrow.value,
            0
          );
          break;
        }
      }
    };

    const addSubrowToMainRows = (
      newRecord: IssuanceProductionDataSubRow,
      deviceId: number
    ) => {
      // Find the mainRow for this device
      const mainRow = mainRows.value.find(
        (row) => row.registry_device_id === deviceId
      );

      if (mainRow) {
        // Add to existing mainRow
        mainRow.subrows.push(newRecord);

        // Recalculate total value
        mainRow.total_value = mainRow.subrows.reduce(
          (sum, subrow) => sum + subrow.value,
          0
        );
      } else {
        // TODO: Use globalError handler
        console.warn("MainRow not found for device:", deviceId);
      }
    };

    const updateSubrowInMainRows = (recordId: number, updatedRecord: any) => {
      // Find and update the specific subrow in mainRows
      for (const mainRow of mainRows.value) {
        const subrowIndex = mainRow.subrows.findIndex(
          (subrow) => subrow.id === recordId
        );
        if (subrowIndex !== -1) {
          // Update the subrow with new data
          mainRow.subrows[subrowIndex] = {
            ...mainRow.subrows[subrowIndex],
            ...updatedRecord,
          };

          // Recalculate the main row total value
          mainRow.total_value = mainRow.subrows.reduce(
            (sum, subrow) => sum + subrow.value,
            0
          );
          break;
        }
      }
    };

    const exportData = async (
      companyId: number,
      queryParams: IssuanceProductionDataQueryParams = {}
    ): Promise<void> => {
      const params = new URLSearchParams();
      sanitizeQueryParams(params, queryParams);

      const response = await goSolidApi.post(
        `/issuance-reporting/companies/${companyId}/issuance-production-data/export?${params.toString()}`,
        {},
        {
          responseType: "blob",
        }
      );

      // Use utility function to handle download with automatic filename extraction
      downloadFromApiResponse(
        response.data,
        response.headers,
        "issuance_export.xlsx"
      );
    };

    // Selection actions
    const toggleSubrowSelection = (subrowId: number) => {
      if (selectedSubrowIds.value.has(subrowId)) {
        selectedSubrowIds.value.delete(subrowId);
      } else {
        selectedSubrowIds.value.add(subrowId);
      }
    };

    const toggleDeviceSelection = (deviceId: number) => {
      const mainRow = mainRows.value.find(
        (row) => row.registry_device_id === deviceId
      );
      if (!mainRow) return;

      const allSelected = mainRow.subrows.every((subrow) =>
        selectedSubrowIds.value.has(subrow.id)
      );

      if (allSelected) {
        // Deselect all subrows for this device
        mainRow.subrows.forEach((subrow) =>
          selectedSubrowIds.value.delete(subrow.id)
        );
      } else {
        // Select all subrows for this device
        mainRow.subrows.forEach((subrow) =>
          selectedSubrowIds.value.add(subrow.id)
        );
      }
    };

    const clearSelection = () => {
      selectedSubrowIds.value.clear();
    };

    const isDeviceFullySelected = (deviceId: number) => {
      const mainRow = mainRows.value.find(
        (row) => row.registry_device_id === deviceId
      );
      if (!mainRow || mainRow.subrows.length === 0) return false;
      return mainRow.subrows.every((subrow) =>
        selectedSubrowIds.value.has(subrow.id)
      );
    };

    const isDevicePartiallySelected = (deviceId: number) => {
      const mainRow = mainRows.value.find(
        (row) => row.registry_device_id === deviceId
      );
      if (!mainRow || mainRow.subrows.length === 0) return false;

      const selectedCount = mainRow.subrows.filter((subrow) =>
        selectedSubrowIds.value.has(subrow.id)
      ).length;
      return selectedCount > 0 && selectedCount < mainRow.subrows.length;
    };

    const sanitizeQueryParams = (
      params: URLSearchParams,
      queryParams: IssuanceProductionDataQueryParams
    ) => {
      return Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    };

    return {
      // State
      mainRows,
      registryDevices,
      loading,
      error,
      recordLoadingStates,
      selectedSubrowIds,

      // Computed
      selectedCount,

      // Actions
      fetchIssuanceProductionData,
      refreshData,
      updateIssuanceProductionRecord,
      createIssuanceProductionRecord,
      deleteIssuanceProductionRecord,
      exportData,

      // Selection actions
      toggleSubrowSelection,
      toggleDeviceSelection,
      clearSelection,
      isDeviceFullySelected,
      isDevicePartiallySelected,
    };
  }
);
